import axios, { type InternalAxiosRequestConfig } from 'axios';
import { CSRF_HEADER, readCsrfToken } from '@common/utils/csrf';

export const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Pub/sub para 429 (rate limit). O interceptor vive fora da árvore React, então
// não pode usar o hook de toast diretamente; um listener (RateLimitToaster)
// se inscreve aqui e exibe a notificação em tela. O erro segue sendo rejeitado.
type RateLimitListener = (retryAfterSeconds: number | null) => void;
const rateLimitListeners = new Set<RateLimitListener>();

export function onRateLimit(listener: RateLimitListener): () => void {
  rateLimitListeners.add(listener);
  return () => {
    rateLimitListeners.delete(listener);
  };
}

// Anexa header CSRF (double-submit) em métodos mutating.
api.interceptors.request.use(
  (config) => {
    const method = (config.method ?? 'get').toLowerCase();
    if (MUTATING_METHODS.has(method)) {
      const csrf = readCsrfToken();
      if (csrf) {
        config.headers.set(CSRF_HEADER, csrf);
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Refresh single-flight: várias requisições que tomam 401 ao mesmo tempo
// (ex.: ao carregar a página com o access token expirado) devem compartilhar
// UMA única chamada a /auth/refresh. Caso contrário, a 2ª+ chamada apresenta o
// cookie nt_refresh antigo (o novo ainda não foi setado), o backend detecta
// reuso e revoga a família inteira — derrubando a sessão.
let refreshPromise: Promise<void> | null = null;

function refreshOnce(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = api
      .post('/auth/refresh')
      .then(() => undefined)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Em 401, tenta um refresh silencioso (cookie nt_refresh) e re-executa.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as RetriableConfig | undefined;
    const status = error.response?.status;
    const url = originalRequest?.url ?? '';

    if (status === 429) {
      const retryAfter = Number(error.response?.headers?.['retry-after']);
      rateLimitListeners.forEach((listener) =>
        listener(Number.isFinite(retryAfter) ? retryAfter : null),
      );
    }

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !url.includes('/auth/refresh') &&
      !url.includes('/auth/login') &&
      !url.includes('/auth/dev-login')
    ) {
      originalRequest._retry = true;
      try {
        await refreshOnce();
        return api(originalRequest);
      } catch {
        // refresh falhou — deixa o erro original (401) propagar para que
        // o useMe/ProtectedRoute trate. NÃO chamar queryClient.clear() aqui:
        // isso desmonta o useMe e causa loop infinito.
      }
    }

    return Promise.reject(error);
  },
);

export default api;
